import { configure as configureBundleEvalTimings } from '@atlaskit/react-ufo/bundle-eval-timing';
import {
	ModuleLoadingProfiler,
	type InteractionMetrics,
} from '@atlaskit/react-ufo/interaction-metrics';
import { init as platformInit } from '@atlaskit/react-ufo/interaction-metrics-init';
import {
	configure as configureResourceTimings,
	startResourceTimingBuffer,
} from '@atlaskit/react-ufo/resource-timing';
import type {
	AssetsConfigAllAtlassianArgs,
	AssetsConfigPreloadedArgs,
} from '@atlaskit/react-ufo/common';
import { configure as configureSsrTiming } from '@atlaskit/react-ufo/ssr';
import { fg } from '@atlassian/jira-feature-gating';
import getMeta from '@atlassian/jira-get-meta';
import { retrieveAndSetPageLoadTrace } from '@atlassian/jira-retrieve-and-set-page-load-trace/src/index.tsx';
import { getJiraCustomData } from '@atlassian/jira-ufo-jira-additional-payload/src/index.tsx';
import { GlobalReactLooselyLazyProfiler } from '@atlassian/react-loosely-lazy';
import { expVal } from '@atlassian/jira-feature-experiments';
import { jiraBundleEvalTimingsConfig } from './common/utils/jira-bundle-eval-config.tsx';
import { jiraResourceTimingsConfig } from './common/utils/jira-resource-config.tsx';
import { jiraSsrConfig } from './common/utils/jira-ssr-config.tsx';

type Capability = 'feature_flag_access' | 'react_profiler';
type InteractionKind = 'page_load' | 'transition' | 'press' | 'typing' | 'legacy' | 'hover';
type UFONameOverride = {
	readonly [ufoName: string]: {
		readonly [metricKey: string]: string;
	};
};
// Defensively typed, since this is directly user-editable
// and they could delete empty members
type UFOConfigFromFF = {
	readonly doNotAbortActivePressInteraction?: string[];
	readonly doNotAbortActivePressInteractionOnTransition?: string[];
	readonly awaitBM3TTI?: string[];
	readonly ufoNameOverrides?: UFONameOverride;
	readonly rates?: {
		readonly [key: string]: number;
	};
	readonly rules?: readonly {
		readonly test?: string;
		readonly rate?: number;
	}[];
	readonly killswitch?: readonly string[];
	readonly typingMethod?: string;
	readonly capability?: Record<Capability, number>;
	readonly kind?: Record<InteractionKind, number>;
	readonly autoGeneratedRate?: number;
	readonly removePageSegmentsUFOPrefixes?: boolean;
	readonly removeInteractionsUFOPrefixes?: boolean;
};

const getUFOConfigFromStatsig = (): UFOConfigFromFF => {
	try {
		return JSON.parse(expVal('ufo_config', 'value', '{}'));
	} catch (error) {
		// eslint-disable-next-line no-console
		console.error('Failed to get UFO config from Statsig dynamic config:', error);
		return {};
	}
};

function getAdditionalJiraFields(interaction: InteractionMetrics) {
	try {
		return { jira: getJiraCustomData(interaction) };
	} catch (error) {
		return {};
	}
}

function loadJiraAnalyticsWebClient() {
	return import(/* webpackChunkName: "AWC" */ '@atlassian/jira-product-analytics-web-client-async');
}

export default function init() {
	const ufoConfigFromFF = getUFOConfigFromStatsig();

	retrieveAndSetPageLoadTrace(getMeta);

	configureResourceTimings(jiraResourceTimingsConfig);
	startResourceTimingBuffer();

	if (!fg('jira-disable-ufo-bundle-eval-timings')) {
		configureBundleEvalTimings(jiraBundleEvalTimingsConfig);
	}

	configureSsrTiming(jiraSsrConfig);

	GlobalReactLooselyLazyProfiler.current = ModuleLoadingProfiler;

	const perfPushFY25ExperienceRates = {
		'issue-view': 1,
		'new-issue-navigator': 1,
		'classic-board': 1,
		'classic-backlog': 1,
		'next-gen-board': 1,
		'jwm.list-view': 1,
		'jwm.jwm-board': 1,
		'jwm.summary-view': 1,
		dashboard: 1,
		'software-issue-navigator': 1,
		'global-new-issue-navigator': 1,
		'issue-navigator': 1,
		'issue-navigator-classic': 1,
		'issue-navigator-core': 1,
		'servicedesk-issues': 1,
	};

	const ssrWhitelist = [];

	if (fg('add_ssr_placeholder_replacements_to_nin_and_nav')) {
		ssrWhitelist.push(
			'software-issue-navigator',
			'global-new-issue-navigator',
			'issue-navigator-classic',
			'issue-navigator-core',
			'servicedesk-issues',
		);
	}
	if (fg('add_ssr_placeholder_replacements_to_nav_for_iv')) {
		ssrWhitelist.push('issue-view');
	}

	if (fg('add_nav_ssr_placeholder_for_board_and_backlog')) {
		ssrWhitelist.push('classic-board', 'classic-backlog');
	}

	let assetsConfig = {};

	if (fg('new_chr_classification')) {
		assetsConfig = {
			classification: {
				all: true,
				allAtlassian: ({ entry }: AssetsConfigAllAtlassianArgs) => {
					const isAtlassian =
						entry.name.includes('atl-paas.net/') ||
						(entry.name.includes('cloudfront') && entry.name.includes('batch.js'));

					return isAtlassian;
				},
				preloaded: ({ entry, SSRDoneTime }: AssetsConfigPreloadedArgs) => {
					return SSRDoneTime && entry.startTime <= SSRDoneTime;
				},
			},
		};
	}

	platformInit(loadJiraAnalyticsWebClient(), {
		...ufoConfigFromFF,
		enableSegmentHighlighting: fg('ufo_enable_segment_highlighting_jira'),
		enableAdditionalPerformanceMarks: fg('ufo_enable_segment_highlighting_jira'),
		product: 'jira',
		region: getMeta('ajs-region') || 'unknown',
		additionalPayloadData: getAdditionalJiraFields,
		postInteractionLog: {
			enabled: fg('ufo_capture_late_rerenders_jira'),
			rates: perfPushFY25ExperienceRates,
		},
		experimentalInteractionMetrics: {
			enabled: fg('ufo-enable-experimental-holds-jira'),
			rates: perfPushFY25ExperienceRates,
		},
		manuallyTrackReactProfilerMounts: fg('ufo-manually-track-segment-mount-phase-jira'),
		vc: {
			enabled: true,
			heatmapSize: 200,
			oldDomUpdates: false,
			devToolsEnabled: fg('uip_vc_devtools'),
			selectorConfig: {
				id: true,
				role: !fg('uip_disable_vc_role_selector'),
				className: true,
				testId: false, // Cannot be enabled due to some testid attributes containing UGC. Use data-vc instead.
			},
			ssr: fg('uip_vc_ssr'),
			ssrWhitelist,
			stopVCAtInteractionFinish: fg('stop_vc_at_interaction_finish'),
			enabledVCRevisions: {
				all: fg('platform_ufo_vc_observer_new') ? ['fy25.02', 'fy25.03'] : ['fy25.02'],
			},
		},
		enableBetterPageVisibilityApi: true,
		assetsConfig,
	});
}
